/**
 * Bundle Product Component
 * 
 * Handles bundle product logic including:
 * - Fetching bundle data via Section Rendering API
 * - Checking bundle variant availability
 * - Updating add-to-cart button state based on bundle component stock
 * - Updating quantity input max value based on bundle constraints
 * - Tracking allocated quantities when items are added to cart
 */

if (!customElements.get('product-bundle')) {
  customElements.define(
    'product-bundle',
    class ProductBundle extends HTMLElement {
      constructor() {
        super();

        this.bundleData = null;
        this.currentVariantId = this.dataset.variantId;
        this.sectionId = this.dataset.sectionId;
        this.productFormId = this.dataset.productFormId;

        // Store error template for fallback
        this.errorHtml = this.querySelector('template')?.content.firstElementChild?.cloneNode(true);

        // Bind methods
        this.onClickRefresh = this.onClickRefresh.bind(this);
      }

      connectedCallback() {
        this.setupEventListeners();

        if (this.currentVariantId) {
          this.disableAddToCart();
          this.fetchBundleData(this.currentVariantId);
        }
      }

      disconnectedCallback() {
        if (this.buyButtonsObserver) {
          this.buyButtonsObserver.disconnect();
        }
      }

      get rootUrl() {
        let url = this.dataset.rootUrl || '/';
        if (!url.endsWith('/')) {
          url = url + '/';
        }
        return url;
      }

      setupEventListeners() {
        const productForm = document.getElementById(this.productFormId);
        if (productForm) {
          const variantInput = productForm.querySelector('input[name="id"]');
          if (variantInput) {
            variantInput.addEventListener('change', (e) => {
              this.currentVariantId = e.target.value;
              this.disableAddToCart();
              this.fetchBundleData(this.currentVariantId);
            });
          }

          this.buyButtonsObserver = new MutationObserver(() => {
            if (this.bundlePending) this.disableAddToCart();
          });

          const buyButtons = document.getElementById(
            `buy-buttons-${this.dataset.sectionId}`
          );
          if (buyButtons) {
            this.buyButtonsObserver.observe(buyButtons, { childList: true });
          }
        }

        // Listen for quantity changes
        const quantityInput = document.querySelector(
          `[form="${this.productFormId}"][name="quantity"], #${this.productFormId} [name="quantity"]`
        );

        if (quantityInput) {
          quantityInput.addEventListener('change', () => {
            this.updateBundleState();
          });
        }

        document.addEventListener('ajaxProduct:added', () => {
          this.disableAddToCart();
          this.fetchBundleData(this.currentVariantId);
        });
      }

      /**
       * Immediately disable the add-to-cart button and mark a bundle check as pending
       */
      disableAddToCart() {
        this.bundlePending = true;
        const addToCartBtn = document.querySelector(
          `#${this.productFormId} [type="submit"], [form="${this.productFormId}"][type="submit"]`
        );
        if (addToCartBtn) {
          addToCartBtn.setAttribute('disabled', 'disabled');
          addToCartBtn.classList.add('disabled');
          addToCartBtn.dataset.bundleDisabled = 'true';
        }
      }

      /**
       * Fetch bundle data for a specific variant via Section Rendering API
       * @param {string|number} variantId - The variant ID to fetch bundle data for
       */
      fetchBundleData(variantId) {
        if (!variantId) return;

        const bundleSectionUrl = `${this.rootUrl}variants/${variantId}/?section_id=bundle-data`;

        fetch(bundleSectionUrl)
          .then((response) => {
            if (!response.ok) {
              throw new Error(`HTTP error! status: ${response.status}`);
            }
            return response.text();
          })
          .then((text) => {
            const html = new DOMParser().parseFromString(text, 'text/html');
            this.bundlePending = false;
            this.processBundleData(html);
          })
          .catch((e) => {
            console.error('Bundle data fetch error:', e);
            this.bundlePending = false;
            this.renderError();
          });
      }

      /**
       * Process the bundle data from the section response
       */
      processBundleData(html) {
        const preview = html.querySelector('bundle-data-preview');

        if (!preview) {
          this.bundleData = null;
          this.updateBundleState();
          return;
        }

        const isBundle = preview.dataset.isBundle === 'true';
        const hasBundleData = preview.dataset.hasBundleData === 'true';

        if (!isBundle || !hasBundleData) {
          this.bundleData = null;
          this.updateBundleState();
          return;
        }

        // Parse bundle JSON data
        const jsonScript = preview.querySelector('[data-bundle-json]');
        if (jsonScript) {
          try {
            this.bundleData = JSON.parse(jsonScript.textContent);
            this.updateBundleState();
          } catch (e) {
            console.error('Failed to parse bundle JSON:', e);
            this.bundleData = null;
            this.renderError();
          }
        } else {
          this.bundleData = null;
          this.updateBundleState();
        }
      }

      onClickRefresh(evt) {
        evt.preventDefault();
        this.fetchBundleData(this.currentVariantId);
      }

      renderError() {
        // For bundles, errors are non-critical - we just disable bundle restrictions
        this.bundleData = null;
        console.warn('Bundle data unavailable, proceeding without bundle restrictions');
      }

      /**
       * Check if all bundle variants are available for the requested quantity
       */
      bundleVariantsAvailable(requestedQty = 1) {
        if (!this.bundleData || !this.bundleData.bundle_variants) return true;

        return this.bundleData.bundle_variants.every(component => {
          if (component.inventory_policy === 'continue') {
            return true;
          }
          return component.available &&
            component.inventory_quantity >= 0 &&
            component.inventory_quantity >= (component.amount * requestedQty);
        });
      }

      /**
       * Get maximum purchasable quantity based on bundle component stock
       */
      getMaxQuantity() {
        if (!this.bundleData) return null;
        return this.bundleData.max_quantity;
      }

      /**
       * Update the add-to-cart button and quantity input based on bundle state
       */
      updateBundleState() {
        if (!this.bundleData || !this.bundleData.isBundle) {
          // Not a bundle or no data - remove any restrictions
          this.removeRestrictions();
          return;
        }

        const quantityInput = document.querySelector(
          `[form="${this.productFormId}"][name="quantity"], #${this.productFormId} [name="quantity"]`
        );
        const requestedQty = quantityInput ? parseInt(quantityInput.value) || 1 : 1;

        const addToCartBtn = document.querySelector(
          `#${this.productFormId} [type="submit"], [form="${this.productFormId}"][type="submit"]`
        );

        if (!addToCartBtn) return;

        const isAvailable = this.bundleVariantsAvailable(requestedQty);
        const maxQty = this.getMaxQuantity();

        // Update quantity input max attribute if there's a max limit
        if (quantityInput && maxQty !== null) {
          quantityInput.setAttribute('max', maxQty);

          // If current value exceeds max, adjust it
          if (parseInt(quantityInput.value) > maxQty) {
            quantityInput.value = Math.max(1, maxQty);
          }
        } else if (quantityInput) {
          quantityInput.removeAttribute('max');
        }

        // Update add to cart button state
        const buttonSpan = addToCartBtn.querySelector('span');

        if (isAvailable) {
          // Check if button was disabled due to bundle restrictions (not variant unavailability)
          if (addToCartBtn.dataset.bundleDisabled === 'true') {
            addToCartBtn.removeAttribute('disabled');
            addToCartBtn.classList.remove('disabled');
            addToCartBtn.dataset.bundleDisabled = 'false';
          }

          // Restore original button text if we changed it
          if (buttonSpan && buttonSpan.dataset.bundleOriginalText) {
            buttonSpan.textContent = buttonSpan.dataset.bundleOriginalText;
            delete buttonSpan.dataset.bundleOriginalText;
          }
        } else {
          // Store original text before changing
          if (buttonSpan && !buttonSpan.dataset.bundleOriginalText) {
            buttonSpan.dataset.bundleOriginalText = buttonSpan.textContent;
          }

          // Check if we should show stock count or sold out
          if (maxQty !== null && maxQty > 0) {
            // Some stock available, but not enough for requested quantity
            if (buttonSpan) {
              buttonSpan.textContent = window.bundleStrings?.stockCount?.replace('[count]', maxQty) ||
                `Only ${maxQty} available`;
            }
            addToCartBtn.setAttribute('disabled', 'disabled');
            addToCartBtn.classList.add('disabled');
            addToCartBtn.dataset.bundleDisabled = 'true';
          } else if (maxQty === 0) {
            // Completely out of stock
            if (buttonSpan) {
              buttonSpan.textContent = window.variantStrings?.soldOut || 'Sold out';
            }
            addToCartBtn.setAttribute('disabled', 'disabled');
            addToCartBtn.classList.add('disabled');
            addToCartBtn.dataset.bundleDisabled = 'true';
          }
        }

        // Dispatch custom event for other components to react
        this.dispatchEvent(new CustomEvent('bundle:stateChanged', {
          bubbles: true,
          detail: {
            variantId: this.currentVariantId,
            isAvailable,
            maxQuantity: maxQty,
            bundleData: this.bundleData
          }
        }));
      }

      /**
       * Remove bundle restrictions when product is not a bundle
       */
      removeRestrictions() {
        const quantityInput = document.querySelector(
          `[form="${this.productFormId}"][name="quantity"], #${this.productFormId} [name="quantity"]`
        );

        if (quantityInput) {
          quantityInput.removeAttribute('max');
        }

        const addToCartBtn = document.querySelector(
          `#${this.productFormId} [type="submit"], [form="${this.productFormId}"][type="submit"]`
        );

        if (addToCartBtn && addToCartBtn.dataset.bundleDisabled === 'true') {
          addToCartBtn.removeAttribute('disabled');
          addToCartBtn.classList.remove('disabled');
          addToCartBtn.dataset.bundleDisabled = 'false';

          const buttonSpan = addToCartBtn.querySelector('span');
          if (buttonSpan && buttonSpan.dataset.bundleOriginalText) {
            buttonSpan.textContent = buttonSpan.dataset.bundleOriginalText;
            delete buttonSpan.dataset.bundleOriginalText;
          }
        }
      }
    }
  );
}